import { X, Clock, Phone, Scissors, UserRound, Sparkles, CalendarCheck, CalendarX, FileImage, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import PatientAlias from "./PatientAlias.jsx";

// "YYYY-MM-DD HH:mm:ss" -> минуты от полуночи, для расчёта времени окончания по seanceLength.
function parseMinutes(dateStr) {
  const timePart = (dateStr || "").split(" ")[1] || "00:00:00";
  const [h, m] = timePart.split(":").map(Number);
  return h * 60 + (m || 0);
}

function formatClock(totalMin) {
  const h = Math.floor(totalMin / 60) % 24;
  const m = totalMin % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// "79218573433"/"+79218573433" -> "+7 921 857-34-33"; форматы, отличные от 11-значного
// российского номера (иностранные, короткие тестовые), просто выводятся как есть.
function formatPhone(phone) {
  const digits = (phone || "").replace(/\D/g, "");
  if (digits.length !== 11) return phone;
  const d = digits[0] === "7" || digits[0] === "8" ? digits.slice(1) : digits.slice(0, 10);
  return `+7 ${d.slice(0, 3)} ${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8, 10)}`;
}

const ATTENDANCE = {
  2: { label: "Подтвердил", className: "text-blue-600 bg-blue-50" },
  1: { label: "Пришёл", className: "text-emerald-600 bg-emerald-50" },
  0: { label: "Ожидание", className: "text-slate-500 bg-slate-100" },
  "-1": { label: "Не пришёл", className: "text-red-600 bg-red-50" },
};

export default function JournalRecordModal({ record, onStartExam, onClose, onNavigateToPatient }) {
  const attendance = ATTENDANCE[String(record.attendance)];
  const start = parseMinutes(record.date);
  const end = start + (record.seanceLength ? record.seanceLength / 60 : 30);
  const initials = (record.clientName || "?").trim().slice(0, 2).toUpperCase();
  const hasVisitStats = (record.clientSuccessVisits || 0) > 0 || (record.clientFailVisits || 0) > 0;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center"
      onMouseDown={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-[440px] max-w-[95vw] overflow-hidden flex flex-col">
        <div className="flex items-start gap-3 px-5 pt-5 pb-4 border-b border-slate-100">
          <div className="h-12 w-12 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 bg-blue-100 text-blue-700">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-base font-bold text-slate-900 truncate">{record.clientName}<PatientAlias name={record.clientName} alias={record.scanAppPatientAlias} className="text-xs" /></span>
              {record.clientIsNew && (
                <span className="flex items-center gap-0.5 text-[10px] font-semibold text-amber-700 bg-amber-50 rounded-full px-1.5 py-0.5">
                  <Sparkles className="h-2.5 w-2.5" /> Новый клиент
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500">
              <Clock className="h-3.5 w-3.5 text-slate-400" />
              {formatClock(start)}–{formatClock(end)}
              {attendance && (
                <span className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${attendance.className}`}>
                  {attendance.label}
                </span>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7 flex-shrink-0">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="px-5 py-4 flex flex-col gap-3">
          {record.clientPhone && (
            <a href={`tel:${record.clientPhone}`} className="flex items-center gap-1.5 text-sm text-slate-700 hover:text-blue-600 w-fit">
              <Phone className="h-4 w-4 text-slate-400" />
              {formatPhone(record.clientPhone)}
            </a>
          )}
          {record.staffName && (
            <div className="flex items-center gap-1.5 text-sm text-slate-700">
              <UserRound className="h-4 w-4 text-slate-400" />
              {record.staffName}
            </div>
          )}

          <div className="flex items-start gap-1.5 text-sm text-slate-700">
            <Scissors className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
            {record.services?.length ? (
              <div className="flex flex-wrap gap-1">
                {record.services.map((s, i) => (
                  <span key={i} className="text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{s}</span>
                ))}
              </div>
            ) : "Без услуги"}
          </div>

          {hasVisitStats && (
            <div className="flex items-center gap-3 text-xs text-slate-500 pt-1">
              {record.clientSuccessVisits > 0 && (
                <span className="flex items-center gap-1">
                  <CalendarCheck className="h-3.5 w-3.5 text-emerald-500" />
                  {record.clientSuccessVisits} визит{pluralizeVisits(record.clientSuccessVisits)}
                </span>
              )}
              {record.clientFailVisits > 0 && (
                <span className="flex items-center gap-1">
                  <CalendarX className="h-3.5 w-3.5 text-red-400" />
                  {record.clientFailVisits} не пришёл
                </span>
              )}
            </div>
          )}

          {record.scanAppScansCount > 0 && (
            <button
              type="button"
              disabled={!onNavigateToPatient}
              onClick={() => onNavigateToPatient?.(record.scanAppPatientId)}
              className="flex items-center justify-between gap-2 text-left rounded-xl bg-blue-50/60 hover:bg-blue-50 transition-colors px-3 py-2.5 disabled:cursor-default disabled:hover:bg-blue-50/60"
            >
              <span className="flex items-center gap-1.5 text-xs font-medium text-blue-700">
                <FileImage className="h-3.5 w-3.5" />
                Уже есть {record.scanAppScansCount} скан{pluralizeScans(record.scanAppScansCount)} в ScanApp
              </span>
              {onNavigateToPatient && <ArrowRight className="h-3.5 w-3.5 text-blue-400 flex-shrink-0" />}
            </button>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
          <Button type="button" variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="button" onClick={() => onStartExam(record)}>Начать обследование</Button>
        </div>
      </div>
    </div>
  );
}

function pluralizeVisits(n) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "";
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return "а";
  return "ов";
}

function pluralizeScans(n) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "";
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return "а";
  return "ов";
}
